import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { 
  Settings, 
  Upload, 
  Image, 
  CreditCard,
  Key,
  UserPlus,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  AlertCircle,
  Trash2,
  Edit,
  DollarSign,
  FileText,
  Users,
  Activity,
  Clock,
  Lock,
  Unlock,
  Wallet
} from 'lucide-react';

// Validation schemas
const siteSettingsSchema = z.object({
  logo_url: z.string().optional().or(z.literal('')),
  favicon_url: z.string().optional().or(z.literal('')),
});

const paystackSchema = z.object({
  paystack_public_key: z.string().min(1, 'Public key is required'),
  paystack_secret_key: z.string().min(1, 'Secret key is required'),
});

// Pricing validation schema
const pricingSchema = z.object({
  lesson_subscription_price: z.number().min(0, 'Price must be positive').max(10000, 'Price too high'),
  affidavit_power_attorney_price: z.number().min(0, 'Price must be positive').max(10000, 'Price too high'),
  other_documents_price: z.number().min(0, 'Price must be positive').max(10000, 'Price too high'),
  application_support_fee: z.number().min(0, 'Fee must be positive').max(10000, 'Fee too high'),
  promotion_exam_price: z.number().min(0, 'Price must be positive').max(10000, 'Price too high'),
});

const passwordSchema = z.object({
  current_password: z.string().min(8, 'Current password is required'),
  new_password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string().min(8, 'Please confirm your password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

const subAdminSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name too long'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string().min(8, 'Please confirm password'),
  permissions: z.array(z.string()).min(1, 'Select at least one permission'),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

const SiteSettings = () => {
  const [activeTab, setActiveTab] = useState('site');
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [subAdmins, setSubAdmins] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  const [pricing, setPricing] = useState(null);
  const [reportLogo, setReportLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Available permissions for sub-admins
  const availablePermissions = [
    { id: 'manage_terms', label: 'Manage Terms', description: 'Create and manage academic terms' },
    { id: 'manage_subscriptions', label: 'Manage Subscriptions', description: 'View and manage teacher subscriptions' },
    { id: 'manage_users', label: 'Manage Users', description: 'View, edit, and delete user accounts' },
    { id: 'generate_lessons', label: 'Generate Lessons', description: 'Use AI to generate lesson notes' },
    { id: 'manage_assets', label: 'Manage Assets', description: 'Upload commissioner stamps and seals' },
    { id: 'view_stats', label: 'View Statistics', description: 'Access lesson notes statistics and reports' },
    { id: 'manage_seo', label: 'Manage SEO', description: 'Update SEO settings and meta tags' },
    { id: 'private_sector_subscription', label: 'Private Sector Subscription', description: 'Manage private sector teachers lesson notes subscriptions and view revenue' },
  ];

  // Form hooks
  const siteForm = useForm({
    resolver: zodResolver(siteSettingsSchema),
    defaultValues: { logo_url: '', favicon_url: '' }
  });

  const paystackForm = useForm({
    resolver: zodResolver(paystackSchema),
    defaultValues: { paystack_public_key: '', paystack_secret_key: '' }
  });

  const pricingForm = useForm({
    resolver: zodResolver(pricingSchema),
    defaultValues: { 
      lesson_subscription_price: 0, 
      affidavit_power_attorney_price: 0, 
      other_documents_price: 0,
      application_support_fee: 0,
      promotion_exam_price: 0
    }
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' }
  });

  const subAdminForm = useForm({
    resolver: zodResolver(subAdminSchema),
    defaultValues: { name: '', email: '', password: '', confirm_password: '', permissions: [] }
  });

  useEffect(() => {
    fetchSiteSettings();
    fetchSubAdmins();
    fetchPricing();
    fetchReportLogo();
  }, []);

  const fetchReportLogo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/report-logo`, {
        withCredentials: true
      });
      setReportLogo(response.data);
    } catch (error) {
      console.error('Error fetching report logo:', error);
    }
  };

  const handleUploadReportLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo_file', file);
      
      const response = await axios.post(`${API_BASE}/api/admin/upload-report-logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Report logo uploaded successfully');
        fetchReportLogo();
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteReportLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the report logo?')) return;
    
    try {
      const response = await axios.delete(`${API_BASE}/api/admin/report-logo`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success('Report logo deleted');
        setReportLogo(null);
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/site-settings`, {
        withCredentials: true
      });
      setSiteSettings(response.data);
      
      // Update form values
      siteForm.reset({
        logo_url: response.data.logo_url || '',
        favicon_url: response.data.favicon_url || ''
      });
      
      paystackForm.reset({
        paystack_public_key: response.data.paystack_public_key || '',
        paystack_secret_key: response.data.paystack_secret_key || ''
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching site settings:', error);
        toast.error("Failed to fetch site settings");
      }
    }
  };

  const fetchSubAdmins = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/sub-admins`, {
        withCredentials: true
      });
      setSubAdmins(response.data);
    } catch (error) {
      console.error('Error fetching sub-admins:', error);
      toast.error("Failed to fetch sub-admins");
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/paystack-pricing`, {
        withCredentials: true
      });
      setPricing(response.data);
      
      // Update pricing form values
      pricingForm.reset({
        lesson_subscription_price: response.data.lesson_subscription_price || 0,
        affidavit_power_attorney_price: response.data.affidavit_power_attorney_price || 0,
        other_documents_price: response.data.other_documents_price || 0,
        application_support_fee: response.data.application_support_fee || 0,
        promotion_exam_price: response.data.promotion_exam_price || 0
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching pricing:', error);
        toast.error("Failed to fetch pricing settings");
      }
    }
  };

  // Site settings handlers
  const onSiteSettingsSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/admin/site-settings`, data, {
        withCredentials: true
      });
      toast.success("Site settings updated successfully");
      fetchSiteSettings();
    } catch (error) {
      console.error('Error updating site settings:', error);
      toast.error(error.response?.data?.detail || "Failed to update site settings");
    } finally {
      setLoading(false);
    }
  };

  // Paystack settings handlers
  const onPaystackSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/admin/site-settings`, data, {
        withCredentials: true
      });
      toast.success("Paystack settings updated successfully");
      fetchSiteSettings();
    } catch (error) {
      console.error('Error updating Paystack settings:', error);
      toast.error(error.response?.data?.detail || "Failed to update Paystack settings");
    } finally {
      setLoading(false);
    }
  };

  // Pricing handlers
  const onPricingSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/admin/paystack-pricing`, data, {
        withCredentials: true
      });
      toast.success("Pricing updated successfully");
      fetchPricing();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(error.response?.data?.detail || "Failed to update pricing");
    } finally {
      setLoading(false);
    }
  };

  // Password update handlers
  const onPasswordSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/admin/password`, {
        new_password: data.new_password
      }, {
        withCredentials: true
      });
      toast.success("Password updated successfully");
      passwordForm.reset();
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  // Sub-admin handlers
  const onSubAdminSubmit = async (data) => {
    try {
      setLoading(true);
      const submitData = {
        name: data.name,
        email: data.email,
        password: data.password,
        permissions: data.permissions
      };
      
      await axios.post(`${API_BASE}/api/admin/sub-admins`, submitData, {
        withCredentials: true
      });
      
      toast.success("Sub-admin created successfully");
      subAdminForm.reset();
      fetchSubAdmins();
    } catch (error) {
      console.error('Error creating sub-admin:', error);
      toast.error(error.response?.data?.detail || "Failed to create sub-admin");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // File upload handlers
  const LogoDropzone = () => {
    const [uploading, setUploading] = useState(false);
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg']
      },
      maxFiles: 1,
      maxSize: 2 * 1024 * 1024, // 2MB
      disabled: uploading,
      onDrop: async (files) => {
        if (files.length > 0) {
          const file = files[0];
          setUploading(true);
          try {
            // Convert to base64 for storage
            const base64Url = await convertFileToBase64(file);
            siteForm.setValue('logo_url', base64Url);
            toast.success("Logo uploaded successfully! Click 'Save Site Settings' to apply.");
          } catch (error) {
            console.error('Error converting logo to base64:', error);
            toast.error("Failed to process logo image");
          } finally {
            setUploading(false);
          }
        }
      },
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'file-too-large') {
          toast.error("Logo file is too large. Max size is 2MB.");
        } else {
          toast.error("Invalid file type. Please upload PNG, JPG, GIF, or SVG.");
        }
      }
    });

    return (
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <>
            <RefreshCw className="h-8 w-8 mx-auto mb-2 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600">Processing logo...</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {isDragActive ? 'Drop logo here...' : 'Drag & drop logo or click to browse'}
            </p>
            <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, SVG up to 2MB</p>
          </>
        )}
      </div>
    );
  };

  const FaviconDropzone = () => {
    const [uploading, setUploading] = useState(false);
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/*': ['.ico', '.png', '.jpg', '.jpeg', '.gif']
      },
      maxFiles: 1,
      maxSize: 1 * 1024 * 1024, // 1MB
      disabled: uploading,
      onDrop: async (files) => {
        if (files.length > 0) {
          const file = files[0];
          setUploading(true);
          try {
            // Convert to base64 for storage
            const base64Url = await convertFileToBase64(file);
            siteForm.setValue('favicon_url', base64Url);
            toast.success("Favicon uploaded successfully! Click 'Save Site Settings' to apply.");
          } catch (error) {
            console.error('Error converting favicon to base64:', error);
            toast.error("Failed to process favicon image");
          } finally {
            setUploading(false);
          }
        }
      },
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'file-too-large') {
          toast.error("Favicon file is too large. Max size is 1MB.");
        } else {
          toast.error("Invalid file type. Please upload ICO, PNG, or JPG.");
        }
      }
    });

    return (
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <>
            <RefreshCw className="h-8 w-8 mx-auto mb-2 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600">Processing favicon...</p>
          </>
        ) : (
          <>
            <Image className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {isDragActive ? 'Drop favicon here...' : 'Drag & drop favicon or click to browse'}
            </p>
            <p className="text-xs text-gray-500 mt-1">ICO, PNG, JPG up to 1MB</p>
          </>
        )}
      </div>
    );
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Site Settings & Configuration
          </CardTitle>
          <CardDescription>
            Manage site configuration, payment settings, security, and administrative access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="site" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Site Assets
              </TabsTrigger>
              <TabsTrigger value="paystack" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Paystack
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="password" className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                Password
              </TabsTrigger>
              <TabsTrigger value="admins" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Sub-Admins
              </TabsTrigger>
            </TabsList>

            {/* Site Assets Tab */}
            <TabsContent value="site" className="space-y-6">
              <form onSubmit={siteForm.handleSubmit(onSiteSettingsSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Site Logo</Label>
                    <LogoDropzone />
                    
                    {/* Logo Preview */}
                    {siteForm.watch('logo_url') && (
                      <div className="border rounded-lg p-3 bg-gray-50">
                        <Label className="text-sm text-gray-600 mb-2 block">Preview:</Label>
                        <img 
                          src={siteForm.watch('logo_url')} 
                          alt="Logo preview" 
                          className="max-h-16 max-w-full object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        {siteForm.watch('logo_url')?.startsWith('data:') && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Image uploaded (will be saved as base64)
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="logo_url">Logo URL (or upload above)</Label>
                      <Input
                        id="logo_url"
                        placeholder="Upload image or enter URL"
                        {...siteForm.register('logo_url')}
                        className={siteForm.watch('logo_url')?.startsWith('data:') ? 'text-gray-400 text-xs' : ''}
                      />
                      {siteForm.watch('logo_url')?.startsWith('data:') && (
                        <p className="text-xs text-gray-500">Base64 image data (uploaded via dropzone)</p>
                      )}
                      {siteForm.formState.errors.logo_url && (
                        <p className="text-sm text-red-600">{siteForm.formState.errors.logo_url.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Favicon Upload */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Favicon</Label>
                    <FaviconDropzone />
                    
                    {/* Favicon Preview */}
                    {siteForm.watch('favicon_url') && (
                      <div className="border rounded-lg p-3 bg-gray-50">
                        <Label className="text-sm text-gray-600 mb-2 block">Preview:</Label>
                        <img 
                          src={siteForm.watch('favicon_url')} 
                          alt="Favicon preview" 
                          className="max-h-8 max-w-full object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        {siteForm.watch('favicon_url')?.startsWith('data:') && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Image uploaded (will be saved as base64)
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="favicon_url">Favicon URL (or upload above)</Label>
                      <Input
                        id="favicon_url"
                        placeholder="Upload image or enter URL"
                        {...siteForm.register('favicon_url')}
                        className={siteForm.watch('favicon_url')?.startsWith('data:') ? 'text-gray-400 text-xs' : ''}
                      />
                      {siteForm.watch('favicon_url')?.startsWith('data:') && (
                        <p className="text-xs text-gray-500">Base64 image data (uploaded via dropzone)</p>
                      )}
                      {siteForm.formState.errors.favicon_url && (
                        <p className="text-sm text-red-600">{siteForm.formState.errors.favicon_url.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="flex items-center gap-2">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {loading ? 'Saving...' : 'Save Site Settings'}
                  </Button>
                </div>
              </form>

              {/* Report Logo Section (Separate from main form) */}
              <div className="border-t pt-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold">Official Report Logo</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a logo that will appear on all SISO official reports. This logo will replace the placeholder on report cover pages.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Upload Section */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Upload Logo</Label>
                    <div 
                      className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors"
                      onClick={() => document.getElementById('report-logo-input').click()}
                    >
                      <Upload className="h-10 w-10 text-purple-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload logo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPEG, or WebP (recommended: 200x200px)</p>
                      <input 
                        id="report-logo-input"
                        type="file" 
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleUploadReportLogo(e.target.files[0]);
                          }
                        }}
                      />
                    </div>
                    {uploadingLogo && (
                      <div className="flex items-center gap-2 text-purple-600">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Uploading...</span>
                      </div>
                    )}
                  </div>

                  {/* Preview Section */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Current Logo</Label>
                    {reportLogo?.has_logo ? (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <img 
                          src={reportLogo.logo_url} 
                          alt="Report Logo" 
                          className="max-h-32 max-w-full object-contain mx-auto mb-3"
                        />
                        <div className="text-center">
                          <p className="text-sm text-gray-600">{reportLogo.filename}</p>
                          <p className="text-xs text-gray-400">
                            Uploaded: {new Date(reportLogo.uploaded_at).toLocaleDateString()}
                          </p>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="mt-3"
                            onClick={handleDeleteReportLogo}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove Logo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-6 bg-gray-50 text-center">
                        <div className="w-24 h-24 mx-auto border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center mb-3">
                          <Image className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500">No logo uploaded</p>
                        <p className="text-xs text-gray-400 mt-1">A placeholder will be shown on reports</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Paystack Tab */}
            <TabsContent value="paystack" className="space-y-6">
              <Alert className="border-blue-200 bg-blue-50">
                <CreditCard className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Configure your Paystack API keys for processing subscription payments. 
                  Get your keys from the <strong>Paystack Dashboard → Settings → API Keys</strong>.
                </AlertDescription>
              </Alert>

              <form onSubmit={paystackForm.handleSubmit(onPaystackSubmit)} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="paystack_public_key">Paystack Public Key</Label>
                    <Input
                      id="paystack_public_key"
                      placeholder="pk_test_..."
                      {...paystackForm.register('paystack_public_key')}
                    />
                    {paystackForm.formState.errors.paystack_public_key && (
                      <p className="text-sm text-red-600">{paystackForm.formState.errors.paystack_public_key.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="paystack_secret_key">Paystack Secret Key</Label>
                    <div className="relative">
                      <Input
                        id="paystack_secret_key"
                        type={showPasswords.paystack_secret ? 'text' : 'password'}
                        placeholder="sk_test_..."
                        {...paystackForm.register('paystack_secret_key')}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('paystack_secret')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.paystack_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {paystackForm.formState.errors.paystack_secret_key && (
                      <p className="text-sm text-red-600">{paystackForm.formState.errors.paystack_secret_key.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="flex items-center gap-2">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    {loading ? 'Saving...' : 'Save Paystack Settings'}
                  </Button>
                </div>
              </form>

              {/* Current Status */}
              {siteSettings?.paystack_public_key && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Paystack Configured</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Payment processing is enabled. Public key: {siteSettings.paystack_public_key.substring(0, 12)}...
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing" className="space-y-6">
              <Alert className="border-amber-200 bg-amber-50">
                <DollarSign className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Set prices for lesson note subscriptions and legal document generation. 
                  These prices will be used in Paystack payment processing.
                </AlertDescription>
              </Alert>

              <form onSubmit={pricingForm.handleSubmit(onPricingSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="h-5 w-5 text-blue-600" />
                        Lesson Notes Subscription
                      </CardTitle>
                      <CardDescription>
                        Monthly subscription price for teachers to access lesson note features
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label htmlFor="lesson_subscription_price">Price (GHS)</Label>
                        <Input
                          id="lesson_subscription_price"
                          type="number"
                          step="0.01"
                          min="0"
                          max="10000"
                          placeholder="0.00"
                          {...pricingForm.register('lesson_subscription_price', { valueAsNumber: true })}
                        />
                        {pricingForm.formState.errors.lesson_subscription_price && (
                          <p className="text-sm text-red-600">{pricingForm.formState.errors.lesson_subscription_price.message}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Current: GHS {pricing?.lesson_subscription_price?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Shield className="h-5 w-5 text-purple-600" />
                        Affidavit, Power of Attorney & Statutory Declaration
                      </CardTitle>
                      <CardDescription>
                        Price for generating affidavit, power of attorney, and statutory declaration documents
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label htmlFor="affidavit_power_attorney_price">Price (GHS)</Label>
                        <Input
                          id="affidavit_power_attorney_price"
                          type="number"
                          step="0.01"
                          min="0"
                          max="10000"
                          placeholder="0.00"
                          {...pricingForm.register('affidavit_power_attorney_price', { valueAsNumber: true })}
                        />
                        {pricingForm.formState.errors.affidavit_power_attorney_price && (
                          <p className="text-sm text-red-600">{pricingForm.formState.errors.affidavit_power_attorney_price.message}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Current: GHS {pricing?.affidavit_power_attorney_price?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FileText className="h-5 w-5 text-green-600" />
                      Other Legal Documents
                    </CardTitle>
                    <CardDescription>
                      Price for generating all other legal documents (contracts, agreements, etc.)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-w-md">
                      <Label htmlFor="other_documents_price">Price (GHS)</Label>
                      <Input
                        id="other_documents_price"
                        type="number"
                        step="0.01"
                        min="0"
                        max="10000"
                        placeholder="0.00"
                        {...pricingForm.register('other_documents_price', { valueAsNumber: true })}
                      />
                      {pricingForm.formState.errors.other_documents_price && (
                        <p className="text-sm text-red-600">{pricingForm.formState.errors.other_documents_price.message}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Current: GHS {pricing?.other_documents_price?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Application Handling Support Fee */}
                <Card className="border-amber-200 bg-amber-50/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Wallet className="h-5 w-5 text-amber-600" />
                      Application Handling Support Fee
                    </CardTitle>
                    <CardDescription>
                      Fee charged when applications are escalated from SISO to Directorate. 
                      This fee is credited to the Director's wallet.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-w-md">
                      <Label htmlFor="application_support_fee">Fee per Application (GHS)</Label>
                      <Input
                        id="application_support_fee"
                        type="number"
                        step="0.01"
                        min="0"
                        max="10000"
                        placeholder="0.00"
                        {...pricingForm.register('application_support_fee', { valueAsNumber: true })}
                      />
                      {pricingForm.formState.errors.application_support_fee && (
                        <p className="text-sm text-red-600">{pricingForm.formState.errors.application_support_fee.message}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Current: GHS {pricing?.application_support_fee?.toFixed(2) || '0.00'}
                      </p>
                      <div className="mt-3 p-3 bg-amber-100/50 rounded-lg text-sm text-amber-800">
                        <p className="font-medium">How it works:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1 text-xs">
                          <li>Teacher/Headteacher pays this fee via Paystack when SISO escalates to Director</li>
                          <li>Fee is automatically credited to Director's wallet</li>
                          <li>Director can request withdrawal to Mobile Money</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Promotion Exam Price */}
                <Card className="border border-gray-200">
                  <CardContent className="pt-5">
                    <div>
                      <Label htmlFor="promotion_exam_price">Promotion Exams Subscription Price (GHS)</Label>
                      <Input
                        id="promotion_exam_price"
                        type="number"
                        step="0.01"
                        min="0"
                        className="mt-1"
                        placeholder="e.g., 50.00"
                        {...pricingForm.register('promotion_exam_price', { valueAsNumber: true })}
                      />
                      {pricingForm.formState.errors.promotion_exam_price && (
                        <p className="text-sm text-red-600">{pricingForm.formState.errors.promotion_exam_price.message}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Current: GHS {pricing?.promotion_exam_price?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="flex items-center gap-2">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                    {loading ? 'Updating...' : 'Update Pricing'}
                  </Button>
                </div>
              </form>

              {/* Pricing Summary */}
              {pricing && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      Current Pricing Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-gray-700">Lesson Subscription</p>
                        <p className="text-lg font-bold text-blue-600">GHS {pricing.lesson_subscription_price?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Affidavit & Power of Attorney</p>
                        <p className="text-lg font-bold text-purple-600">GHS {pricing.affidavit_power_attorney_price?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Other Documents</p>
                        <p className="text-lg font-bold text-green-600">GHS {pricing.other_documents_price?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Application Support Fee</p>
                        <p className="text-lg font-bold text-amber-600">GHS {pricing.application_support_fee?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                      Last updated: {pricing.updated_at ? new Date(pricing.updated_at).toLocaleDateString() : 'Not set'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Password Tab */}
            <TabsContent value="password" className="space-y-6">
              <Alert className="border-amber-200 bg-amber-50">
                <Shield className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Update your admin password regularly for security. Use a strong password with at least 8 characters.
                </AlertDescription>
              </Alert>

              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                <div className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current_password"
                        type={showPasswords.current ? 'text' : 'password'}
                        {...passwordForm.register('current_password')}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.current_password && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.current_password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new_password"
                        type={showPasswords.new ? 'text' : 'password'}
                        {...passwordForm.register('new_password')}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.new_password && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.new_password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm_password"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        {...passwordForm.register('confirm_password')}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.confirm_password && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.confirm_password.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="flex items-center gap-2">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Sub-Admins Tab */}
            <TabsContent value="admins" className="space-y-6">
              {/* Create Sub-Admin Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Create Sub-Administrator
                  </CardTitle>
                  <CardDescription>
                    Create a new sub-administrator with specific permissions and access rights
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={subAdminForm.handleSubmit(onSubAdminSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="sub_name">Full Name</Label>
                          <Input
                            id="sub_name"
                            placeholder="Enter full name"
                            {...subAdminForm.register('name')}
                          />
                          {subAdminForm.formState.errors.name && (
                            <p className="text-sm text-red-600">{subAdminForm.formState.errors.name.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="sub_email">Email Address</Label>
                          <Input
                            id="sub_email"
                            type="email"
                            placeholder="Enter email address"
                            {...subAdminForm.register('email')}
                          />
                          {subAdminForm.formState.errors.email && (
                            <p className="text-sm text-red-600">{subAdminForm.formState.errors.email.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="sub_password">Password</Label>
                          <div className="relative">
                            <Input
                              id="sub_password"
                              type={showPasswords.sub_password ? 'text' : 'password'}
                              placeholder="Create password"
                              {...subAdminForm.register('password')}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility('sub_password')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPasswords.sub_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {subAdminForm.formState.errors.password && (
                            <p className="text-sm text-red-600">{subAdminForm.formState.errors.password.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="sub_confirm_password">Confirm Password</Label>
                          <div className="relative">
                            <Input
                              id="sub_confirm_password"
                              type={showPasswords.sub_confirm ? 'text' : 'password'}
                              placeholder="Confirm password"
                              {...subAdminForm.register('confirm_password')}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility('sub_confirm')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPasswords.sub_confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {subAdminForm.formState.errors.confirm_password && (
                            <p className="text-sm text-red-600">{subAdminForm.formState.errors.confirm_password.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Label className="text-base font-medium">Permissions</Label>
                        <div className="space-y-3 max-h-64 overflow-y-auto border rounded-lg p-4">
                          {availablePermissions.map((permission) => (
                            <div key={permission.id} className="flex items-start space-x-3">
                              <input
                                type="checkbox"
                                id={permission.id}
                                value={permission.id}
                                {...subAdminForm.register('permissions')}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <label htmlFor={permission.id} className="text-sm font-medium cursor-pointer">
                                  {permission.label}
                                </label>
                                <p className="text-xs text-gray-600">{permission.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {subAdminForm.formState.errors.permissions && (
                          <p className="text-sm text-red-600">{subAdminForm.formState.errors.permissions.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={loading} className="flex items-center gap-2">
                        {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                        {loading ? 'Creating...' : 'Create Sub-Admin'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Existing Sub-Admins List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Existing Sub-Administrators
                  </CardTitle>
                  <CardDescription>
                    Manage existing sub-administrators and their permissions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {subAdmins.length > 0 ? (
                    <div className="space-y-4">
                      {subAdmins.map((admin) => (
                        <div key={admin.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{admin.name}</h4>
                                <Badge variant="secondary">Sub-Admin</Badge>
                                {admin.is_active ? (
                                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                                ) : (
                                  <Badge variant="destructive">Inactive</Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{admin.email}</p>
                              <div className="flex flex-wrap gap-1">
                                {admin.permissions.map((permission) => (
                                  <Badge key={permission} variant="outline" className="text-xs">
                                    {availablePermissions.find(p => p.id === permission)?.label || permission}
                                  </Badge>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500">
                                Created: {new Date(admin.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="flex items-center gap-1">
                                <Edit className="h-3 w-3" />
                                Edit
                              </Button>
                              <Button size="sm" variant="outline" className="flex items-center gap-1 text-red-600 hover:text-red-700">
                                <Trash2 className="h-3 w-3" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Sub-Administrators</h3>
                      <p className="text-gray-600">Create your first sub-administrator to delegate admin tasks.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteSettings;